import { ATMOSPHERE, NOISE, WORLD_UNIFORMS } from './chunks';

export const FULLSCREEN_VS = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

// ------------------------------------------------------------------------------------------- sky dome

export const SKY_VS = /* glsl */ `
varying vec3 vDir;
void main() {
  vec4 wPos = modelMatrix * vec4(position, 1.0);
  vDir = wPos.xyz - cameraPosition;
  gl_Position = projectionMatrix * viewMatrix * wPos;
  gl_Position.z = gl_Position.w * 0.99999;
}
`;

export const SKY_FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform float uCloudBand;
uniform float uSunDisk;
varying vec3 vDir;
void main() {
  vec3 dir = normalize(vDir);
  vec3 col = skyGradient(dir);
  float sd = dot(dir, uSunDir);
  col += uSunColor * smoothstep(0.99935, 0.99972, sd) * 3.5 * uSunDisk;
  if (dir.y > -0.03) {
    vec2 uv = dir.xz / (dir.y + 0.11);
    float t = uTime * 0.006;
    float n = fbm2(uv * 0.75 + vec2(t, t * 0.3));
    float streak = fbm2(uv * vec2(0.28, 1.9) + vec2(30.0 + t * 2.0, 4.0));
    float cl = smoothstep(0.5, 0.86, n * 0.62 + streak * 0.55);
    float band = smoothstep(-0.03, 0.08, dir.y) * (1.0 - smoothstep(0.28, 0.85, dir.y));
    cl *= band * uCloudBand;
    float lit = 0.6 + 0.4 * smoothstep(0.45, 0.9, n);
    vec3 cc = mix(uSkyHorizon, vec3(1.0), 0.55) * lit + uSunGlow * pow(max(sd, 0.0), 5.0) * 0.6;
    cc = mix(cc, uShadowTint * 1.3, (1.0 - lit) * 0.35);
    col = mix(col, cc, cl * 0.85);
  }
  // Melt into the same haze the world's fog uses so there is never a seam at the horizon.
  float haze = exp(-max(dir.y + 0.02, 0.0) * 14.0);
  col = mix(col, fogColorFor(dir), haze * 0.85);
  gl_FragColor = vec4(col, 1.0);
}
`;

// ------------------------------------------------------------------------------------------- cloud noise (3D, tileable Perlin-Worley)

export const CLOUD_NOISE_FS = /* glsl */ `
precision highp float;
uniform float uSlice;
varying vec2 vUv;

vec3 h33(vec3 p) {
  p = vec3(dot(p, vec3(127.1, 311.7, 74.7)), dot(p, vec3(269.5, 183.3, 246.1)), dot(p, vec3(113.5, 271.9, 124.6)));
  return fract(sin(p) * 43758.5453123);
}

// Tileable gradient noise with period 'per' cells.
float gradNoise(vec3 x, float per) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float r = 0.0;
  float n[8];
  for (int k = 0; k < 8; k++) {
    vec3 o = vec3(float(k & 1), float((k >> 1) & 1), float((k >> 2) & 1));
    vec3 g = normalize(h33(mod(i + o, per)) * 2.0 - 1.0);
    n[k] = dot(g, f - o);
  }
  return mix(mix(mix(n[0], n[1], u.x), mix(n[2], n[3], u.x), u.y), mix(mix(n[4], n[5], u.x), mix(n[6], n[7], u.x), u.y), u.z);
}

float worley(vec3 x, float per) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  float d = 1e9;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int xx = -1; xx <= 1; xx++) {
    vec3 o = vec3(float(xx), float(y), float(z));
    vec3 pnt = h33(mod(i + o, per));
    vec3 diff = o + pnt - f;
    d = min(d, dot(diff, diff));
  }
  return 1.0 - sqrt(d);
}

float worleyFbm(vec3 p, float freq) {
  return worley(p * freq, freq) * 0.625 + worley(p * freq * 2.0, freq * 2.0) * 0.25 + worley(p * freq * 4.0, freq * 4.0) * 0.125;
}

float perlinFbm(vec3 p, float freq) {
  float s = 0.0;
  float a = 1.0;
  float norm = 0.0;
  for (int o = 0; o < 5; o++) {
    s += a * gradNoise(p * freq, freq);
    norm += a;
    a *= 0.5;
    freq *= 2.0;
  }
  return s / norm * 0.5 + 0.5;
}

float remap(float v, float a, float b, float c, float d) { return c + (v - a) / (b - a) * (d - c); }

float stretch(float v, float mean, float k) { return clamp((v - mean) * k + 0.5, 0.0, 1.0); }

void main() {
  vec3 p = vec3(vUv, uSlice);
  // Raw fbm/worley values cluster tightly; stretch them to use the full 0..1 range so the
  // cloud shader's coverage threshold produces real gaps and billows.
  float pf = stretch(perlinFbm(p, 4.0), 0.5, 2.6);
  float w1 = stretch(worleyFbm(p, 4.0), 0.47, 2.2);
  float w2 = stretch(worleyFbm(p, 8.0), 0.46, 2.2);
  float w3 = stretch(worleyFbm(p, 16.0), 0.48, 2.2);
  // Perlin-Worley: billowy (worley) cells modulated by wispy (perlin) structure.
  float pw = clamp(pf * 0.55 + w1 * 0.45, 0.0, 1.0);
  gl_FragColor = vec4(pw, w1, w2, w3);
}
`;

// ------------------------------------------------------------------------------------------- volumetric clouds

export const CLOUDS_FS = /* glsl */ `
precision highp float;
precision highp sampler3D;
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform sampler2D tDepth;
uniform sampler3D tNoise;
uniform mat4 uInvProj;
uniform mat4 uCamWorld;
uniform vec2 uCamNearFar;
uniform vec4 uCloudSlab;    // bottom, top, coverage, density
uniform vec4 uCloudScale;   // scale, detailScale, windX, windZ
uniform vec3 uAmbTop;
uniform vec3 uAmbBottom;
uniform float uFrame;
varying vec2 vUv;

#ifndef STEPS
#define STEPS 32
#endif
#ifndef LIGHT_STEPS
#define LIGHT_STEPS 4
#endif

float remap(float v, float a, float b, float c, float d) { return c + (v - a) / (b - a) * (d - c); }

float hg(float mu, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * 3.14159265 * pow(1.0 + g2 - 2.0 * g * mu, 1.5));
}

float cloudDensity(vec3 p, bool detail) {
  float h = (p.y - uCloudSlab.x) / (uCloudSlab.y - uCloudSlab.x);
  if (h < 0.0 || h > 1.0) return 0.0;
  vec3 q = p + vec3(uCloudScale.z, 0.0, uCloudScale.w);
  vec4 low = texture(tNoise, q / uCloudScale.x);
  float lowFbm = low.g * 0.625 + low.b * 0.25 + low.a * 0.125;
  float base = low.r * 0.7 + lowFbm * 0.3;
  // Large-scale coverage: wide "continents" of cloud with open breaks between them.
  float cov = texture(tNoise, q / (uCloudScale.x * 5.0) + 0.37).r;
  float coverage = clamp(uCloudSlab.z + (cov - 0.5) * 1.1, 0.0, 1.0);
  // Rounded bottoms; tops rise and fall with coverage so the sea has swells.
  float grad = smoothstep(0.0, 0.14, h) * smoothstep(1.0, 0.35 + cov * 0.45, h);
  float d = remap(base * grad, 1.0 - coverage, 1.0, 0.0, 1.0);
  if (d <= 0.0) return 0.0;
  if (detail) {
    vec4 hi = texture(tNoise, q / uCloudScale.y + vec3(0.0, uTime * 0.004, 0.0));
    float hiFbm = hi.g * 0.625 + hi.b * 0.25 + hi.a * 0.125;
    float erode = mix(hiFbm, 1.0 - hiFbm, clamp(h * 4.0, 0.0, 1.0));
    d = remap(d, erode * 0.3, 1.0, 0.0, 1.0);
  }
  return max(d, 0.0) * uCloudSlab.w;
}

float lightOptical(vec3 p) {
  float od = 0.0;
  float stepL = 6.0;
  vec3 pos = p;
  for (int i = 0; i < LIGHT_STEPS; i++) {
    pos += uSunDir * stepL;
    od += cloudDensity(pos, false) * stepL;
    stepL *= 1.9;
  }
  return od;
}

void main() {
  vec2 uv = vUv;
  vec4 ndc = vec4(uv * 2.0 - 1.0, 1.0, 1.0);
  vec4 vpos = uInvProj * ndc;
  vec3 rdView = normalize(vpos.xyz / vpos.w);
  vec3 rd = normalize((uCamWorld * vec4(rdView, 0.0)).xyz);
  vec3 ro = cameraPosition;

  float depth = texture2D(tDepth, uv).x;
  float sceneDist = 1e9;
  if (depth < 0.99999) {
    float near = uCamNearFar.x;
    float far = uCamNearFar.y;
    float viewZ = (near * far) / ((far - near) * depth - far);
    sceneDist = viewZ / rdView.z;
  }

  float bottom = uCloudSlab.x;
  float top = uCloudSlab.y;
  float t0, t1;
  if (abs(rd.y) < 1e-4) {
    if (ro.y < bottom || ro.y > top) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    t0 = 0.0;
    t1 = 4000.0;
  } else {
    float ta = (bottom - ro.y) / rd.y;
    float tb = (top - ro.y) / rd.y;
    t0 = max(min(ta, tb), 0.0);
    t1 = max(ta, tb);
  }
  t1 = min(t1, min(sceneDist, t0 + 3200.0));
  if (t1 <= t0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }

  float jitter = ign(gl_FragCoord.xy + vec2(uFrame * 5.588238, uFrame * 3.231));
  float t = t0;
  float mu = dot(rd, uSunDir);
  float phase = mix(hg(mu, 0.7), hg(mu, -0.2), 0.3) * 4.0 * 3.14159265;
  vec3 sun = uSunColor;
  float T = 1.0;
  vec3 L = vec3(0.0);
  float firstHit = -1.0;
  bool first = true;

  for (int i = 0; i < STEPS; i++) {
    // Distance-adaptive steps: fine near the camera, coarser far away; big strides through empty air.
    float stepLen = clamp((t - t0) * 0.05 + 5.0, 5.0, 70.0);
    if (first) { stepLen *= jitter; first = false; }
    t += stepLen;
    if (t > t1) break;
    vec3 p = ro + rd * t;
    float dens = cloudDensity(p, true);
    if (dens > 0.0004) {
      if (firstHit < 0.0) firstHit = t;
      float od = lightOptical(p);
      // Multiple-scattering approximation (Wrenninge): octaves with weakened extinction and flatter phase.
      vec3 scatter = vec3(0.0);
      float a = 1.0;
      float b = 1.0;
      float cph = 1.0;
      for (int o = 0; o < 3; o++) {
        scatter += a * exp(-od * b) * mix(1.0, phase, cph);
        a *= 0.5;
        b *= 0.35;
        cph *= 0.5;
      }
      // Beer-Powder: dark rims, luminous creases.
      float powder = 1.0 - exp(-od * 2.0 - dens * 30.0);
      float h = clamp((p.y - bottom) / (top - bottom), 0.0, 1.0);
      vec3 amb = mix(uAmbBottom, uAmbTop, smoothstep(0.0, 1.0, h));
      vec3 lum = sun * scatter * mix(0.35, 1.0, powder) * 0.52 + amb * 0.9;
      float sampleT = exp(-dens * stepLen);
      L += T * lum * (1.0 - sampleT);
      T *= sampleT;
      if (T < 0.02) break;
    } else {
      t += stepLen * 0.5;
    }
  }

  // Aerial perspective on the clouds themselves.
  if (firstHit > 0.0) {
    vec3 hitPos = ro + rd * firstHit;
    vec3 dir;
    float f = fogAmount(hitPos, dir);
    vec3 fc = fogColorFor(rd);
    L = mix(L, fc * (1.0 - T), f * 0.85);
  }
  gl_FragColor = vec4(L, T);
}
`;

// ------------------------------------------------------------------------------------------- bloom (dual Kawase)

export const SCENE_SAMPLE = /* glsl */ `
uniform sampler2D tScene;
#ifdef CLOUDS
uniform sampler2D tClouds;
#endif
vec3 sceneAt(vec2 uv) {
  vec3 c = texture2D(tScene, uv).rgb;
  #ifdef CLOUDS
    vec4 cl = texture2D(tClouds, uv);
    c = c * cl.a + cl.rgb;
  #endif
  return c;
}
`;

export const BLOOM_PREFILTER_FS = /* glsl */ `
${SCENE_SAMPLE}
uniform vec2 uTexel;
uniform float uThreshold;
varying vec2 vUv;
void main() {
  vec3 c = sceneAt(vUv + uTexel * vec2(-1.0, -1.0)) + sceneAt(vUv + uTexel * vec2(1.0, -1.0)) +
           sceneAt(vUv + uTexel * vec2(-1.0, 1.0)) + sceneAt(vUv + uTexel * vec2(1.0, 1.0));
  c *= 0.25;
  float br = max(c.r, max(c.g, c.b));
  float soft = clamp(br - uThreshold + 0.5, 0.0, 1.0);
  soft = soft * soft * 0.5;
  float contrib = max(soft, br - uThreshold) / max(br, 1e-4);
  gl_FragColor = vec4(min(c * contrib, vec3(16.0)), 1.0);
}
`;

export const KAWASE_DOWN_FS = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec3 s = texture2D(tInput, vUv).rgb * 4.0;
  s += texture2D(tInput, vUv + uTexel * vec2(-1.0, -1.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(1.0, -1.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(-1.0, 1.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(1.0, 1.0)).rgb;
  gl_FragColor = vec4(s / 8.0, 1.0);
}
`;

export const KAWASE_UP_FS = /* glsl */ `
uniform sampler2D tInput;
uniform sampler2D tBase;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec3 s = vec3(0.0);
  s += texture2D(tInput, vUv + uTexel * vec2(-2.0, 0.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(2.0, 0.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(0.0, -2.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(0.0, 2.0)).rgb;
  s += texture2D(tInput, vUv + uTexel * vec2(-1.0, -1.0)).rgb * 2.0;
  s += texture2D(tInput, vUv + uTexel * vec2(1.0, -1.0)).rgb * 2.0;
  s += texture2D(tInput, vUv + uTexel * vec2(-1.0, 1.0)).rgb * 2.0;
  s += texture2D(tInput, vUv + uTexel * vec2(1.0, 1.0)).rgb * 2.0;
  gl_FragColor = vec4(s / 12.0 + texture2D(tBase, vUv).rgb, 1.0);
}
`;

// ------------------------------------------------------------------------------------------- sun shafts

/**
 * Screen-space god rays (after Mitchell, GPU Gems 3 ch. 13): march from each pixel toward the
 * sun's screen position accumulating "open sky" samples with decay. Runs at quarter resolution.
 * R = shaft intensity.
 */
export const SHAFTS_FS = /* glsl */ `
uniform sampler2D tDepth;
uniform vec2 uSunUv;
uniform float uAspect;
uniform float uFrame;
varying vec2 vUv;
float ignS(vec2 p) { return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715)))); }
float skyMask(vec2 uv) {
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return 0.0;
  float d = texture2D(tDepth, uv).r;
  // Open sky only, weighted by closeness to the sun disc.
  vec2 dv = (uv - uSunUv) * vec2(uAspect, 1.0);
  float glow = exp(-dot(dv, dv) * 9.0);
  return step(0.99999, d) * (0.25 + glow);
}
void main() {
  const int STEPS = 40;
  vec2 delta = (vUv - uSunUv) * (0.96 / float(STEPS));
  vec2 uv = vUv - delta * ignS(gl_FragCoord.xy + uFrame * 17.0);
  float decay = 1.0;
  float sum = 0.0;
  for (int i = 0; i < STEPS; i++) {
    sum += skyMask(uv) * decay;
    decay *= 0.955;
    uv -= delta;
  }
  vec2 dv = (vUv - uSunUv) * vec2(uAspect, 1.0);
  float falloff = exp(-length(dv) * 1.6);
  gl_FragColor = vec4(sum / float(STEPS) * falloff, 0.0, 0.0, 1.0);
}
`;

// ------------------------------------------------------------------------------------------- final composite

export const FINAL_FS = /* glsl */ `
${SCENE_SAMPLE}
${NOISE}
uniform sampler2D tBloom;
uniform float uBloom;
uniform vec3 uBloomTint;
uniform float uGrain;
#ifdef FLARE
uniform sampler2D tDepth;
uniform vec3 uFlare;        // sun uv, strength (on screen, facing it)
uniform vec3 uFlareColor;
#endif
uniform vec2 uResolution;
uniform vec2 uFocus;
uniform float uSpeed;       // 0..1
uniform float uTime;
uniform float uStorm;       // 0..1
uniform vec3 uStormColor;
uniform vec3 uStormGlow;
uniform float uStormKind;   // 0 squall, 1 sandstorm
uniform float uExposure;
uniform float uSaturation;
uniform float uContrast;
uniform vec3 uGradeShadows;
uniform vec3 uGradeHighlights;
uniform float uVignette;
uniform float uFlash;
uniform vec3 uFlashColor;
uniform float uFade;        // 0 = black, 1 = scene
uniform float uDesat;       // crash desaturation
uniform vec3 uHero;         // pearl screen uv + radius: kept crisp
#ifdef SHAFTS
uniform sampler2D tShafts;
uniform vec3 uShaftColor;   // sun colour × strength × visibility
#endif
varying vec2 vUv;

#ifndef TAPS
#define TAPS 8
#endif

vec3 neutralTonemap(vec3 color) {
  const float startCompression = 0.8 - 0.04;
  const float desaturation = 0.15;
  float x = min(color.r, min(color.g, color.b));
  float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
  color -= offset;
  float peak = max(color.r, max(color.g, color.b));
  if (peak < startCompression) return color;
  const float d = 1.0 - startCompression;
  float newPeak = 1.0 - d * d / (peak + d - startCompression);
  color *= newPeak / peak;
  float g = 1.0 - 1.0 / (desaturation * (peak - newPeak) + 1.0);
  return mix(color, newPeak * vec3(1.0), g);
}

vec3 toSRGB(vec3 c) {
  c = max(c, 0.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

#ifdef FLARE
// How much of the sun's disc is open sky — behind a hill, a tree or thick cloud, no flare.
float sunOpen(float aspect) {
  float v = 0.0;
  for (int i = 0; i < 5; i++) {
    vec2 o = i == 0 ? vec2(0.0) : vec2(i == 1 ? 1.0 : i == 2 ? -1.0 : 0.0, i == 3 ? 1.0 : i == 4 ? -1.0 : 0.0);
    vec2 q = uFlare.xy + o * vec2(0.007 / aspect, 0.007);
    if (q.x < 0.0 || q.y < 0.0 || q.x > 1.0 || q.y > 1.0) continue;
    float open = step(0.99999, texture2D(tDepth, q).r);
    #ifdef CLOUDS
      open *= texture2D(tClouds, q).a;
    #endif
    v += open;
  }
  return v / 5.0;
}

// A camera lens catching the low sun: soft ghosts strung along the line from the sun through
// the centre of the frame, a faint halo ring and a thin horizontal streak. Quiet, not sci-fi.
vec3 lensFlare(vec2 uv, float aspect) {
  vec2 sun = uFlare.xy;
  vec2 axis = vec2(0.5) - sun;
  vec2 A = vec2(aspect, 1.0);
  vec3 c = vec3(0.0);
  // (position along the axis ×2 = mirrored through the centre, radius, tint, strength)
  vec4 g[6];
  g[0] = vec4(0.32, 0.028, 0.0, 0.10);
  g[1] = vec4(0.55, 0.05, 1.0, 0.06);
  g[2] = vec4(0.78, 0.018, 0.0, 0.12);
  g[3] = vec4(1.12, 0.075, 2.0, 0.05);
  g[4] = vec4(1.45, 0.04, 1.0, 0.07);
  g[5] = vec4(1.9, 0.13, 2.0, 0.035);
  for (int i = 0; i < 6; i++) {
    vec2 p = sun + axis * 2.0 * g[i].x;
    float d = length((uv - p) * A);
    float r = g[i].y;
    // Soft disc with a slightly brighter rim, like an aperture ghost.
    float disc = smoothstep(r, r * 0.8, d) * (0.65 + 0.35 * smoothstep(r * 0.5, r * 0.95, d));
    vec3 tint = g[i].z < 0.5 ? vec3(1.0, 0.86, 0.7) : g[i].z < 1.5 ? vec3(0.75, 0.95, 0.85) : vec3(0.75, 0.82, 1.0);
    c += tint * disc * g[i].w;
  }
  float hd = length((uv - sun) * A);
  float halo = exp(-pow((hd - 0.3) / 0.025, 2.0)) * 0.03;
  c += mix(vec3(1.0, 0.75, 0.6), vec3(0.6, 0.8, 1.0), smoothstep(0.28, 0.32, hd)) * halo;
  float sy = (uv.y - sun.y);
  float sx = (uv.x - sun.x) * aspect;
  c += vec3(1.0, 0.92, 0.85) * exp(-sy * sy * 5200.0) * exp(-abs(sx) * 2.6) * 0.2;
  return c;
}
#endif

void main() {
  vec2 uv = vUv;
  float aspect = uResolution.x / uResolution.y;
  vec2 toC = uv - uFocus;
  vec2 toCa = toC * vec2(aspect, 1.0);
  float r = length(toCa);

  // Radial speed blur that only bites toward the edges — the centre stays crisp.
  float edge = smoothstep(0.2, 0.9, r);
  float hero = smoothstep(uHero.z, uHero.z * 2.6, length((uv - uHero.xy) * vec2(aspect, 1.0)));
  float blur = uSpeed * edge * 0.085 * hero;
  vec3 col;
  // Blur shorter than ~half a pixel (standing still, the screen centre, the pearl) is one tap:
  // the full loop only runs where it shows. Neighbouring pixels branch the same way.
  if (length(toC * blur * uResolution) < 0.5) {
    col = sceneAt(uv);
  } else {
    float jit = ign(gl_FragCoord.xy + fract(uTime * 7.0) * 61.0);
    col = vec3(0.0);
    for (int i = 0; i < TAPS; i++) {
      float t = (float(i) + jit) / float(TAPS);
      col += sceneAt(uv - toC * blur * t);
    }
    col /= float(TAPS);
  }

  // Chromatic fringe at speed.
  float ca = uSpeed * edge * 0.006 * hero;
  if (ca > 0.001) {
    col.r = mix(col.r, sceneAt(uv - toC * (blur * 0.5 + ca)).r, 0.5);
    col.b = mix(col.b, sceneAt(uv - toC * (blur * 0.5 - ca)).b, 0.5);
  }

  #ifdef FLARE
    if (uFlare.z > 0.001) col += lensFlare(uv, aspect) * uFlareColor * uFlare.z * sunOpen(aspect);
  #endif
  #ifdef BLOOM
    // Soft, wide glow tinted by the light (halation), like highlights blooming on film.
    col += texture2D(tBloom, uv).rgb * uBloom * uBloomTint;
  #endif
  #ifdef SHAFTS
    col += uShaftColor * texture2D(tShafts, uv).r;
  #endif

  // Painterly speed lines streaming out of the focus point (only at speed).
  if (uSpeed > 0.35) {
    float ang = atan(toCa.y, toCa.x);
    float lanes = 110.0;
    float a = (ang / 6.2831853 + 0.5) * lanes;
    float lane = floor(a);
    float lf = fract(a);
    float rnd = hash12(vec2(lane, 7.13));
    float rnd2 = hash12(vec2(lane, 1.37));
    float spd = 0.9 + rnd2 * 1.6;
    float head = fract(rnd * 3.7 + uTime * spd * (0.6 + uSpeed));
    float seg = smoothstep(0.0, 0.08, r * 0.9 - head * 0.9 + 0.12) * smoothstep(0.32, 0.0, r * 0.9 - head * 0.9 + 0.12);
    float width = 0.06 + r * 0.05;
    float lineMask = smoothstep(width, 0.0, abs(lf - 0.5)) * seg;
    float laneOn = step(1.0 - uSpeed * 0.42, rnd2);
    float lines = lineMask * laneOn * smoothstep(0.22, 0.6, r) * smoothstep(0.35, 1.0, uSpeed);
    col = mix(col, vec3(1.0), lines * 0.38);
  }

  // The storm closing in from the screen edges: billowing, self-shadowed cloud masses that
  // churn toward the camera, with lightning (squall) or grit (haboob).
  if (uStorm > 0.001) {
    vec2 dir = toCa / max(r, 1e-3);
    float t = uTime;
    float n1 = fbm2(dir * 2.4 + vec2(r * 3.0 - t * 0.9, t * 0.21));
    float n2 = fbm2(dir * 5.2 + vec2(-t * 0.35, r * 6.0 - t * 2.1) + n1 * 1.6);
    float billow = n1 * 0.55 + n2 * 0.45;
    float reach = 1.0 - uStorm * 0.7;
    float m = smoothstep(reach - 0.12, reach + 0.1, r + (billow - 0.5) * 0.45);
    vec3 belly = uStormColor * 0.3;
    vec3 tops = uStormColor * 0.95 + uStormGlow * 0.1;
    vec3 sc = mix(belly, tops, smoothstep(0.38, 0.78, n2 + (n1 - 0.5) * 0.4));
    float streak = smoothstep(0.62, 0.95, vnoise(vec2(dot(dir, vec2(37.0, 91.0)) + dir.x * 23.0, r * 3.0 - t * 7.0)));
    sc += uStormGlow * streak * 0.12 * m;
    if (uStormKind > 0.5) {
      float grit = hash12(floor(gl_FragCoord.xy * 0.5) + floor(t * 30.0));
      sc += uStormGlow * grit * 0.14;
    } else {
      float bolt = step(0.985, hash12(vec2(floor(t * 8.0), 3.0))) * uStorm;
      sc += uStormGlow * bolt * 1.1 * smoothstep(0.45, 0.85, n2);
    }
    float fringe = smoothstep(reach - 0.24, reach - 0.06, r) * (1.0 - m);
    col = mix(col, sc, m * clamp(uStorm * 1.35, 0.0, 1.0));
    col = mix(col, col * mix(vec3(1.0), uStormColor * 1.6, 0.4), fringe * uStorm * 0.6);
  }

  // Grade.
  col *= uExposure;
  col = neutralTonemap(col);
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(lum), col, uSaturation * (1.0 - uDesat));
  col = (col - 0.5) * uContrast + 0.5;
  col *= mix(uGradeShadows, vec3(1.0), smoothstep(0.0, 0.55, lum)) ;
  col = mix(col, col * uGradeHighlights, smoothstep(0.5, 1.0, lum));
  col = mix(col, uFlashColor, uFlash);
  float vig = smoothstep(1.25, 0.35, length((uv - 0.5) * vec2(aspect * 0.8, 1.0)));
  col *= mix(1.0, vig, uVignette);
  // Fine film grain, strongest in the mid-tones, never in the deep shadows or highlights.
  float gl = dot(col, vec3(0.2126, 0.7152, 0.0722));
  float grain = hash12(floor(gl_FragCoord.xy) + fract(uTime * 13.0) * vec2(97.0, 41.0)) - 0.5;
  col += grain * uGrain * 4.0 * gl * (1.0 - gl);
  col = clamp(col, 0.0, 1.0) * uFade;
  vec3 outc = toSRGB(col);
  outc += (ign(gl_FragCoord.xy) - 0.5) / 255.0;
  gl_FragColor = vec4(outc, 1.0);
}
`;
